import { createContext, useEffect, useState } from 'react';
import { PortfolioDataInput } from '~/types';
import { Fund, FundsContextType, FundsProviderProps } from './types';

const initialFunds: Fund[] = [
    {
        name: 'Standard Life Future Advantage 1',
        externalLink: 'https://library.standardlife.co.uk/MJMN.pdf',
        description: 'Download fund fact sheet',
        amc: '0.98%',
        citicode: 'VFUS',
        inceptionDate: '2021-12-06',
    },
    {
        name: 'Standard Life Future Advantage 2',
        externalLink: 'https://library.standardlife.co.uk/NIAE.pdf',
        description: 'Download fund fact sheet',
        amc: '0.98%',
        citicode: 'WHQF',
        inceptionDate: '2021-12-06',
    },
    {
        name: 'Standard Life Future Advantage 3',
        externalLink: 'https://library.standardlife.co.uk/IABB.pdf',
        description: 'Download fund fact sheet',
        amc: '0.98%',
        citicode: 'H09R',
        inceptionDate: '2021-12-06',
    },
    {
        name: 'Standard Life Future Advantage 4',
        externalLink: 'https://library.standardlife.co.uk/IDEC.pdf',
        description: 'Download fund fact sheet',
        amc: '0.98%',
        citicode: 'WLM2',
        inceptionDate: '2021-12-06',
    },
    {
        name: 'Standard Life Future Advantage 5',
        externalLink: 'https://library.standardlife.co.uk/EBLG.pdf',
        description: 'Download fund fact sheet',
        amc: '0.98%',
        citicode: 'UX8S',
        inceptionDate: '2021-12-06',
    },
];

export const FundsContext = createContext<FundsContextType | undefined>(undefined);

export function FundsProvider({ children }: FundsProviderProps) {
    const [funds, setFunds] = useState<Fund[]>(initialFunds);
    const [selectedFunds, setSelectedFunds] = useState<Fund[]>([]);
    const [portfolioData, setPortfolioData] = useState<PortfolioDataInput | undefined>(undefined);

    // The fetchFunds function is a scaffold for the future API implementation.
    // Currently, it simulates an asynchronous API call by resolving the hardcoded initialFunds array after a short delay.
    // In the future, this function will be updated to fetch the appropriate funds from the actual API,
    // based on the criteria for selecting which funds should be retrieved.
    const fetchFunds = async () => {
        const apiFunds = await new Promise<Fund[]>(resolve => setTimeout(() => resolve(initialFunds), 100));
        setFunds(apiFunds);
    };

    const updateSelectedFunds = (fund: Fund, isChecked: boolean, value: string) => {
        setSelectedFunds(prev => {
            if (isChecked && value) {
                const exists = prev.some(f => f.name === fund.name);
                if (!exists) {
                    return [...prev, { ...fund, allocation: value }];
                }
                return prev.map(f => (f.name === fund.name ? { ...f, allocation: value } : f));
            } else {
                return prev.filter(f => f.name !== fund.name);
            }
        });
    };

    useEffect(() => {
        fetchFunds();
    }, []);

    const resetFunds = () => {
        setSelectedFunds([]);
    };

    return (
        <FundsContext.Provider
            value={{
                funds,
                selectedFunds,
                portfolioData,
                updateSelectedFunds,
                fetchFunds,
                setPortfolioData,
                resetFunds,
            }}
        >
            {children}
        </FundsContext.Provider>
    );
}
